<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useGifPlayer } from '@/composables/useGifPlayer';

const props = defineProps<{
  src: string;
}>();

const emit = defineEmits<{
  loaded: [];
  click: [event: MouseEvent];
}>();

const canvasEl = ref<HTMLCanvasElement | null>(null);
const {
  frameCount,
  currentFrame,
  isPlaying,
  isLoading,
  play,
  pause,
  toggle,
  seekTo,
} = useGifPlayer(computed(() => props.src), canvasEl);

// Notify parent once frames are available (fires exactly once per src lifetime).
watch(frameCount, (count) => { if (count > 0) emit('loaded'); }, { once: true });

function onSeek(event: Event) {
  seekTo((event.target as HTMLInputElement).valueAsNumber);
}

defineExpose({ toggle, pause, play, seekTo, currentFrame, frameCount, isPlaying });
</script>

<template>
  <div class="gif-player">
    <canvas
      ref="canvasEl"
      class="gif-canvas"
      @click="emit('click', $event)"
    />
    <div
      v-if="!isLoading && frameCount > 1"
      class="gif-controls"
    >
      <button
        class="gif-play-pause"
        :aria-label="isPlaying ? 'Pause GIF' : 'Play GIF'"
        @click.stop="toggle()"
      >
        {{ isPlaying ? '⏸' : '▶' }}
      </button>
      <span class="gif-frame-counter">{{ currentFrame + 1 }}&thinsp;/&thinsp;{{ frameCount }}</span>
      <input
        class="gif-seek"
        type="range"
        :min="0"
        :max="frameCount - 1"
        :value="currentFrame"
        @input="onSeek"
        @click.stop
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.gif-player {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.gif-canvas {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
}

.gif-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.75rem;
}

.gif-play-pause {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
  padding: 0 4px;
  flex-shrink: 0;
  line-height: 1;
}

.gif-frame-counter {
  flex-shrink: 0;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.gif-seek {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  accent-color: var(--accent, #7f7);
}
</style>
